import useSWR from "swr";
import type {
  CachedQueryName,
  CachedQueryTypes,
} from "../graphql/cachedQueries";
import { CacheMode, cachedGqlKey } from "../utils/cachedGqlKey";

export type { CacheMode };

type Options<Name extends CachedQueryName> = {
  variables?: CachedQueryTypes[Name]["variables"];
  // "server": in-memory cache per Next.js process
  // "browser": Cache-Control headers (browser / CDN)
  mode: CacheMode;
  // Cache lifetime in seconds (server: in-memory freshness, browser: max-age).
  // Defaults to the API route's default (24h).
  ttl?: number;
  skip?: boolean;
  // Keep returning the previous variables' data while new variables load (e.g. after
  // the visitor's currency replaces the default one), instead of a loading state.
  keepPreviousData?: boolean;
  // Refetch on mount (e.g. coming back to the page client-side) when the data in
  // memory is older than `ttl`. Without it, data already in memory is reused for the
  // rest of the session.
  revalidateIfStale?: boolean;
};

const fetcher = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return res.json();
};

// Fetches an allowlisted query (see graphql/cachedQueries.ts) through a cached API route.
// Data prefetched in getStaticProps (see graphql/prefetchCachedGql.ts) is used as the
// initial value, so no loading state is shown on those pages.
export function useCachedGql<Name extends CachedQueryName>(
  op: Name,
  {
    variables,
    mode,
    ttl,
    skip = false,
    keepPreviousData = false,
    revalidateIfStale = false,
  }: Options<Name> = { mode: "server" },
) {
  const { data, error, isLoading, mutate } = useSWR<
    CachedQueryTypes[Name]["data"]
  >(
    skip
      ? null
      : // With revalidateIfStale the data must be fresh once `ttl` has passed, so the
        // browser's HTTP cache must not answer with the expired response either.
        cachedGqlKey(op, variables, mode, ttl, !revalidateIfStale),
    fetcher,
    {
      revalidateOnFocus: false,
      // By default, don't refetch on mount when data already exists (ISR fallback or an
      // earlier fetch); the page's ISR revalidation keeps the fallback fresh enough.
      revalidateIfStale,
      // With revalidateIfStale, a mount within `ttl` of the last fetch reuses it, so
      // the data is refetched only once it is older than `ttl`.
      ...(revalidateIfStale &&
        ttl !== undefined && {
          dedupingInterval: ttl * 1000,
        }),
      keepPreviousData,
    },
  );

  // SWR reports isLoading during the background revalidation even when fallback data
  // exists; only treat it as loading when there is nothing to render yet.
  return {
    data,
    error,
    isLoading: isLoading && data === undefined,
    mutate,
  };
}
